import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import type { SelectChangeEvent } from '@mui/material'
import {
  Avatar,
  Box,
  Checkbox,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { FaFolder } from 'react-icons/fa'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import CustomChip from '@/components/Chip'
import { IconButton } from '@/components/IconButton'
import { useUpdateCaseDetailMutation } from '@/store/apiService'
import { statusLabelMap, statusOptions } from '@/utils/constants'
import { getStatusStyle } from '@/utils/helper'

import type { ProjectStatus } from '.'

interface Project {
  id: string
  name: string
  status: 'active' | 'scheduled' | 'archive'
  dentist?: {
    name: string
  }
  image?: {
    image: string
  }
  shared_with: string[]
  created_by?: string
  role?: string
  created_ts: string
  updated_ts: string
  updated_by?: string
}

interface ProjectRowProps {
  row: Project
  index: number
  handleStatusChange: (index: number, newStatus: ProjectStatus) => void
  handleMenuOpen: (event: React.MouseEvent<HTMLElement>, index: number) => void
  tabValue: number
  isSelected: boolean
  onCheckboxToggle: () => void
  refetch: () => void
}

const ProjectRow = ({
  row,
  index,
  handleMenuOpen,
  tabValue,
  isSelected,
  onCheckboxToggle,
  refetch,
}: ProjectRowProps) => {
  const { bgColor, textColor } = getStatusStyle(row.status)
  const isShared = tabValue === 1
  const [updateCaseDetail] = useUpdateCaseDetailMutation()
  const { t } = useTranslation('translation', {
    keyPrefix: 'projectsTable.toast',
  })

  const handleSelectChange = async (event: SelectChangeEvent<unknown>) => {
    const newStatus = event.target.value as Project['status']

    try {
      await updateCaseDetail({ caseId: row.id, status: newStatus }).unwrap()
      refetch()
      toast.success(t('statusUpdated'))
    } catch (error) {
      toast.error(t('statusUpdateFailed'))
    }
  }

  const navigate = useNavigate()
  const location = useLocation()
  const isTrashPage = location.pathname === '/projects/trash'

  const handleRowClick = () => {
    navigate(`/projects/${row.id}?caseId=${row.id}`, {
      state: { project: row },
    })
  }

  return (
    <TableRow
      sx={{
        '&:hover': {
          bgcolor: 'rgba(217, 217, 217, 0.05)',
        },
        cursor: isTrashPage ? 'default' : 'pointer',
      }}
      onClick={!isTrashPage ? handleRowClick : undefined}
    >
      {!isShared && (
        <TableCell
          padding="checkbox"
          sx={textStyle}
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            sx={checkboxStyle}
            disableRipple
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation()
              onCheckboxToggle()
            }}
          />
        </TableCell>
      )}

      <TableCell sx={{ ...textStyle, whiteSpace: 'nowrap' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Avatar
            variant="square"
            style={{
              borderRadius: '8px',
              height: '60px',
              width: '60px',
              background: 'rgba(189, 189, 189, 1)',
              cursor: 'pointer',
            }}
            src={row?.image?.image || ''}
          >
            <FaFolder size="20px" color="white" />
          </Avatar>
          <Typography sx={{ px: 2, whiteSpace: 'wrap', fontSize: '14px' }}>
            {row.name}
          </Typography>
        </Box>
      </TableCell>

      <TableCell onClick={(e) => e.stopPropagation()}>
        {isShared || isTrashPage ? (
          <CustomChip
            label={statusLabelMap[row?.status]}
            sx={{
              bgcolor: bgColor,
              color: textColor,
              '&:hover': {
                bgcolor: bgColor,
              },
              padding: '4px',
            }}
            size="medium"
          />
        ) : (
          <Select
            value={row.status}
            onChange={(e) => {
              e.stopPropagation()
              handleSelectChange(e)
            }}
            size="small"
            variant="outlined"
            sx={{
              ...textStyle,
              backgroundColor: bgColor,
              color: textColor,
              fontWeight: 500,
              borderRadius: '20px',
              '& .MuiSelect-icon': { color: textColor },
              '& fieldset': { border: 'none' },
              '& .MuiSelect-select': {
                padding: '4px 12px',
                whiteSpace: 'nowrap',
              },
              '& .MuiTypography-root': {
                color: textColor,
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: '0.17px',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: '#2a2a2a',
                  ...textStyle,
                },
              },
            }}
          >
            {statusOptions.map((option) => {
              const { textColor } = getStatusStyle(option)
              return (
                <MenuItem key={option} value={option} sx={textStyle}>
                  <Typography sx={{ color: textColor, fontWeight: 500 }}>
                    {statusLabelMap[option]}
                  </Typography>
                </MenuItem>
              )
            })}
          </Select>
        )}
      </TableCell>

      <TableCell sx={{ ...textStyle, maxWidth: '155px' }}>
        {row.dentist?.name}
      </TableCell>

      <TableCell sx={textStyle}>
        {(() => {
          if (isShared) {
            return row?.role?.charAt(0).toUpperCase() + row?.role?.slice(1)
          }

          if ((row.shared_with?.length || 0) <= 3) {
            return row.shared_with?.map((user) => (
              <Typography
                key={user}
                sx={{ ...textStyle, whiteSpace: 'nowrap' }}
              >
                {user}
              </Typography>
            ))
          }

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box>
                {row.shared_with?.slice(0, 3)?.map((user) => (
                  <Typography
                    key={user}
                    sx={{
                      ...textStyle,
                      whiteSpace: 'nowrap',
                      display: 'block',
                    }}
                  >
                    {user}
                  </Typography>
                ))}
              </Box>
              <Tooltip
                title={
                  <Box component="span" sx={{ whiteSpace: 'pre-line' }}>
                    {row.shared_with?.slice(3)?.join('\n')}
                  </Box>
                }
                placement="right-end"
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: '#292929',
                      fontSize: '14px',
                      fontWeight: 500,
                      lineHeight: '20px',
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255, 255, 255, 0.5)',
                    color: 'black',
                    borderRadius: '100px',
                    height: '24px',
                    fontSize: '13px',
                    fontWeight: 400,
                    mt: 0.5,
                    width: '35px',
                    cursor: 'pointer',
                  }}
                >
                  +{row.shared_with?.length - 3}
                </Box>
              </Tooltip>
            </Box>
          )
        })()}
      </TableCell>

      <TableCell sx={textStyle}>
        {moment.utc(row.created_ts).format('MMM Do, YYYY, h:mmA [UTC]')}
      </TableCell>

      {isShared && <TableCell sx={textStyle}>{row.created_by}</TableCell>}

      <TableCell sx={textStyle}>
        {moment.utc(row.updated_ts).format('MMM Do, YYYY, h:mmA [UTC]')}
        {row.updated_by && (
          <>
            <br />({row.updated_by})
          </>
        )}
      </TableCell>

      <TableCell align="right">
        <IconButton
          sx={{ bgcolor: '#76768033', padding: '4px 8px' }}
          onClick={(e) => {
            e.stopPropagation()
            handleMenuOpen(e, index)
          }}
        >
          <MoreHorizIcon sx={{ color: 'white' }} />
        </IconButton>
      </TableCell>
    </TableRow>
  )
}

const checkboxStyle = {
  color: 'white',
  '&.Mui-checked': { color: '#90caf9' },
  '& .MuiSvgIcon-root': { fontSize: 20 },
}

const textStyle = {
  color: 'white',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '143%',
  letterSpacing: '0.17px',
}

export default ProjectRow
